import React, { useState, useEffect, useContext } from "react";
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TablePagination,
  TableRow, Container, Typography, Avatar, Chip, IconButton, Collapse,
  Grid, Divider, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, Stack,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { collection, getDocs, doc, updateDoc, deleteDoc, setDoc } from "firebase/firestore";
import { db } from "../database/db";
import Page from "../components/Page";
import Scrollbar from "../components/Scrollbar";
import { TableTitle, TableHeader } from "../components/_dashboard/user";
import { SearchContext } from "../Context/searchContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TABLE_HEAD = [
  { id: "expand", label: "", alignRight: false },
  { id: "avatar", label: "Photo", alignRight: false },
  { id: "name", label: "Name / Username", alignRight: false },
  { id: "email", label: "Email", alignRight: false },
  { id: "status", label: "Status", alignRight: false },
  { id: "brokerage", label: "Brokerage", alignRight: false },
  { id: "violations", label: "Violations", alignRight: false },
  { id: "actions", label: "Actions", alignRight: true },
];

function UserDetailRow({ user, onAction }) {
  return (
    <Box sx={{ p: 3, bgcolor: "background.default", borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3} sx={{ textAlign: "center" }}>
          <Avatar src={user.profileImage} sx={{ width: 100, height: 100, mx: "auto", mb: 1 }}>
            {(user.name || user.username || "?").charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{user.name || user.username}</Typography>
          <Typography variant="body2" color="text.secondary">@{user.username}</Typography>
          {user.tagline && (
            <Typography variant="caption" sx={{ fontStyle: "italic", color: "text.secondary", display: "block", mt: 0.5 }}>
              "{user.tagline}"
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Account Details</Typography>
          <Divider sx={{ mb: 1 }} />
          {[
            ["Email", user.email || "—"],
            ["Phone", user.phone || "—"],
            ["Sign-up Method", user.signUpMethod || "—"],
            ["Joined", user.createdAt?.toDate?.()?.toLocaleDateString() || "—"],
            ["Brokerage", user.brokerageConnected ? "Connected ✓" : "Not Connected"],
            ["Brokerage Type", user.brokerageType || "—"],
            ["Subscription", user.subscriptionTier || "Free"],
            ["Token Balance", user.tokenBalance ?? 0],
          ].map(([label, val]) => (
            <Box key={label} sx={{ display: "flex", mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, width: 130, color: "text.secondary" }}>{label}:</Typography>
              <Typography variant="caption">{val}</Typography>
            </Box>
          ))}
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Actions</Typography>
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={1}>
            <Button size="small" variant="outlined" startIcon={<Icon icon="eva:message-square-fill" />}
              onClick={() => onAction("dm", user)}>
              Send DM
            </Button>
            <Button size="small" variant="outlined" color="warning" startIcon={<Icon icon="eva:lock-fill" />}
              onClick={() => onAction("password", user)}>
              Reset Password
            </Button>
            <Button size="small" variant="outlined" color={user.suspended ? "success" : "error"}
              startIcon={<Icon icon={user.suspended ? "eva:checkmark-circle-fill" : "eva:slash-fill"} />}
              onClick={() => onAction(user.suspended ? "unblock" : "block", user)}>
              {user.suspended ? "Unsuspend" : "Suspend (7 days)"}
            </Button>
            <Button size="small" variant="outlined" color="error" startIcon={<Icon icon="fe:trash" />}
              onClick={() => onAction("delete", user)}>
              Delete Account
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [search, setSearch] = useContext(SearchContext);
  const [pwDialog, setPwDialog] = useState({ open: false, user: null, newPw: "" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, user: null });

  useEffect(() => {
    loadUsers();
    return () => setSearch("");
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "User"));
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      toast.error("Failed to load users");
    }
    setLoading(false);
  };

  const handleAction = (action, user) => {
    if (action === "password") {
      setPwDialog({ open: true, user, newPw: "" });
      return;
    }
    if (action === "dm") {
      toast.info("DM feature coming soon — integrate from the app side");
      return;
    }
    setConfirmDialog({ open: true, action, user });
  };

  const executeAction = async () => {
    const { action, user } = confirmDialog;
    setConfirmDialog({ open: false, action: null, user: null });
    try {
      const ref = doc(db, "User", user.id || user.uid);
      if (action === "block") {
        const suspendUntil = new Date();
        suspendUntil.setDate(suspendUntil.getDate() + 7);
        await updateDoc(ref, { suspended: true, suspendedUntil: suspendUntil.toISOString(), status: false });
        toast.success(`${user.name || user.username} suspended for 7 days`);
      } else if (action === "unblock") {
        await updateDoc(ref, { suspended: false, suspendedUntil: null, status: true });
        toast.success(`${user.name || user.username} unsuspended`);
      } else if (action === "delete") {
        await deleteDoc(ref);
        toast.success("User deleted");
      }
      loadUsers();
    } catch (err) {
      toast.error("Action failed: " + err.message);
    }
  };

  const handlePasswordReset = async () => {
    const { user, newPw } = pwDialog;
    if (!newPw || newPw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      // Store reset request in Firestore — app reads this and prompts user to change
      await setDoc(doc(db, "PasswordResets", user.id || user.uid), {
        uid: user.id || user.uid,
        newPassword: newPw,
        requestedAt: new Date().toISOString(),
        requestedBy: "admin",
      });
      toast.success("Password reset queued for user");
      setPwDialog({ open: false, user: null, newPw: "" });
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  const filtered = users.filter((u) => {
    const s = search?.toLowerCase() || "";
    return (
      (u.name || "").toLowerCase().includes(s) ||
      (u.username || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const aVal = a[orderBy] || "";
    const bVal = b[orderBy] || "";
    return order === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Page title="User Management | Fire Investing Admin">
      <ToastContainer position="top-right" />
      <Container maxWidth="xl">
        <Box sx={{ pb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>User Management</Typography>
          <Typography variant="body2" color="text.secondary">
            {users.length} total users · Click a row to expand
          </Typography>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
          <Scrollbar>
            <TableContainer>
              <Table>
                <TableTitle title="Fire Investing Users" numSelected={0} />
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={users.length}
                  numSelected={0}
                  onRequestSort={(e, prop) => {
                    setOrder(orderBy === prop && order === "asc" ? "desc" : "asc");
                    setOrderBy(prop);
                  }}
                />
                <TableBody>
                  {paginated.map((user) => {
                    const uid = user.id || user.uid;
                    const isExpanded = expandedRow === uid;
                    const isSuspended = user.suspended;
                    const isActive = user.status === true || user.status === "active";
                    return (
                      <React.Fragment key={uid}>
                        <TableRow
                          hover
                          onClick={() => setExpandedRow(isExpanded ? null : uid)}
                          sx={{ cursor: "pointer", bgcolor: isExpanded ? "rgba(61,193,14,0.08)" : "inherit" }}
                        >
                          <TableCell>
                            <IconButton size="small" onClick={() => setExpandedRow(isExpanded ? null : uid)}>
                              <Icon icon={isExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} />
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Avatar src={user.profileImage} sx={{ width: 36, height: 36 }}>
                              {(user.name || user.username || "?").charAt(0).toUpperCase()}
                            </Avatar>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {user.name || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              @{user.username || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>{user.email || "—"}</TableCell>
                          <TableCell>
                            <Chip
                              label={isSuspended ? "Suspended" : isActive ? "Active" : "Inactive"}
                              size="small"
                              color={isSuspended ? "error" : isActive ? "success" : "default"}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.brokerageConnected ? "Connected" : "None"}
                              size="small"
                              color={user.brokerageConnected ? "primary" : "default"}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.violations || 0}
                              size="small"
                              color={(user.violations || 0) > 0 ? "warning" : "default"}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Suspend 7 days">
                              <IconButton size="small" color="warning"
                                onClick={(e) => { e.stopPropagation(); handleAction(isSuspended ? "unblock" : "block", user); }}>
                                <Icon icon={isSuspended ? "eva:checkmark-circle-fill" : "eva:slash-fill"} width={18} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error"
                                onClick={(e) => { e.stopPropagation(); handleAction("delete", user); }}>
                                <Icon icon="fe:trash" width={18} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={8} sx={{ p: 0, border: isExpanded ? undefined : "none" }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <UserDetailRow user={user} onAction={handleAction} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                  {paginated.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          {loading ? "Loading users..." : "No users found"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </Card>
      </Container>

      {/* Password Reset Dialog */}
      <Dialog open={pwDialog.open} onClose={() => setPwDialog({ open: false, user: null, newPw: "" })} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Set a new temporary password for <strong>{pwDialog.user?.name || pwDialog.user?.username}</strong>
          </Typography>
          <TextField
            fullWidth label="New Password" type="password"
            value={pwDialog.newPw}
            onChange={(e) => setPwDialog((p) => ({ ...p, newPw: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwDialog({ open: false, user: null, newPw: "" })}>Cancel</Button>
          <Button variant="contained" onClick={handlePasswordReset}>Set Password</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null, user: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmDialog.action === "delete"
              ? `Permanently delete ${confirmDialog.user?.name || confirmDialog.user?.username}? This cannot be undone.`
              : confirmDialog.action === "block"
              ? `Suspend ${confirmDialog.user?.name || confirmDialog.user?.username} for 7 days from the community?`
              : `Unsuspend ${confirmDialog.user?.name || confirmDialog.user?.username}?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null, user: null })}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmDialog.action === "delete" ? "error" : "primary"}
            onClick={executeAction}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
